import { useEffect, useId, useRef, useState } from "react";
import { toast } from "sonner";
import { useProfileStore, getFacilityKeyForPortal, getPatientKey } from "@/store/profile";
import patientRequest, { type IPaymentProfileListResponse } from "@/api/patient-request";
import statementPaymentRequest, {
  type StatementNewCardStartResult,
  type StatementPaymentResult,
} from "@/api/statement-payment-request";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import { loadCommerceJs } from "@/utils/commerceJs";
import { parsePaymentAmount, paymentAmountError, usd } from "@/utils/statement";

const NEW_CARD = -1;

/**
 * Pays all or part of a statement's balance through Fortis: a saved card is charged directly; a new card
 * goes through Fortis's hosted Commerce.js fields, then the API verifies and posts the sale.
 */
export function StatementPaymentForm(props: {
  taskId: number;
  balanceDue: number;
  onPaid: (result: StatementPaymentResult) => void;
}) {
  const { taskId, balanceDue, onPaid } = props;
  const currentPatient = useProfileStore((s) => s.currentPatient);
  const loginEmail = useProfileStore((s) => s.profileAuthInfo?.email ?? "");
  const ids = useId();

  const [amountText, setAmountText] = useState(balanceDue.toFixed(2));
  const [email, setEmail] = useState(loginEmail);
  const [cards, setCards] = useState<IPaymentProfileListResponse[]>([]);
  const [cardId, setCardId] = useState<number>(NEW_CARD);
  const [saveCard, setSaveCard] = useState(false);
  const [cardName, setCardName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [hosted, setHosted] = useState<StatementNewCardStartResult | null>(null);
  /** Set once the patient picks a card, so the saved-card list arriving late doesn't change their choice. */
  const picked = useRef(false);

  useEffect(() => {
    if (!currentPatient) return;
    let cancelled = false;
    patientRequest.request
      .getPaymentProfileList({ facilityID: currentPatient.facilityID, id: currentPatient.patientID })
      .then((res) => {
        if (cancelled) return;
        const fortis = (Array.isArray(res.data) ? res.data : []).filter(
          (c) => c.processor?.toUpperCase() === "FORTIS" && !c.deleted
        );
        setCards(fortis);
        const preferred = fortis.find((c) => c.isDefault) ?? fortis[0];
        if (preferred && !picked.current) setCardId(preferred.id);
      })
      .catch(() => {
        if (!cancelled) setCards([]);
      });
    return () => {
      cancelled = true;
    };
  }, [currentPatient]);

  const keys = () => ({
    facilityKey: getFacilityKeyForPortal() ?? "",
    patientKey: getPatientKey() ?? "",
    taskID: taskId,
  });

  const amount = parsePaymentAmount(amountText);
  const amountProblem = paymentAmountError(amount, balanceDue);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    picked.current = true;
    if (amountProblem || amount == null) {
      setError(amountProblem);
      return;
    }
    setError("");
    setBusy(true);
    try {
      if (cardId !== NEW_CARD) {
        const res = await statementPaymentRequest.request.payWithSavedCard({
          ...keys(),
          amount,
          paymentProfileID: cardId,
          email: email.trim(),
        });
        if (res.data?.success) onPaid(res.data);
        else setError(res.data?.reason || "We couldn't process the payment. Please try again.");
        return;
      }
      const res = await statementPaymentRequest.request.startNewCard({
        ...keys(),
        amount,
        saveCard,
        saveTitle: saveCard ? cardName.trim() : "",
      });
      if (!res.data?.success) {
        setError(res.data?.reason || "We couldn't start the payment. Please try again.");
        return;
      }
      await loadCommerceJs(res.data.scriptUrl);
      setHosted(res.data);
    } catch {
      setError("We couldn't process the payment. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (hosted && amount != null) {
    return (
      <HostedCardFields
        start={hosted}
        amount={amount}
        onCancel={() => setHosted(null)}
        onDone={async (transactionId) => {
          const res = await statementPaymentRequest.request.completeNewCard({
            ...keys(),
            posID: hosted.posID,
            transactionID: transactionId,
            email: email.trim(),
          });
          if (res.data?.success) onPaid(res.data);
          else {
            toast.error(res.data?.reason || "We couldn't confirm the payment. Please contact the office.");
            setHosted(null);
          }
        }}
      />
    );
  }

  return (
    <form onSubmit={handleSubmit} className="mt-5 flex flex-col gap-4 border-t border-healthsource-lightLavender pt-5" noValidate>
      <h3 className="text-[15px] font-extrabold text-healthsource-dark">Make a payment</h3>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${ids}-amount`}>Amount</Label>
        <div className="relative">
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
          <Input
            id={`${ids}-amount`}
            inputMode="decimal"
            autoComplete="off"
            value={amountText}
            onChange={(e) => setAmountText(e.target.value)}
            onBlur={() => amount != null && setAmountText(amount.toFixed(2))}
            className="pl-7"
            aria-describedby={`${ids}-amount-help`}
          />
        </div>
        <p id={`${ids}-amount-help`} className="text-[13px] text-healthsource-gray">
          Pay the full {usd.format(balanceDue)} or any part of it.
        </p>
      </div>

      <fieldset className="flex flex-col gap-2">
        <legend className="mb-1.5 text-sm font-semibold text-slate-900">Card</legend>
        {cards.map((c) => (
          <CardOption
            key={c.id}
            name={`${ids}-card`}
            checked={cardId === c.id}
            onSelect={() => {
              picked.current = true;
              setCardId(c.id);
            }}
            label={c.methodDesc || `${c.paymentMethod || "Card"} ending ${c.accountNo.slice(-4)}`}
            detail={c.description}
          />
        ))}
        <CardOption
          name={`${ids}-card`}
          checked={cardId === NEW_CARD}
          onSelect={() => {
            picked.current = true;
            setCardId(NEW_CARD);
          }}
          label="Use a new card"
        />
        {cardId === NEW_CARD && (
          <label className="flex min-h-11 items-center gap-2 pl-1 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={saveCard}
              onChange={(e) => {
                picked.current = true;
                setSaveCard(e.target.checked);
              }}
              className="h-5 w-5"
            />
            Save this card for future payments
          </label>
        )}
        {cardId === NEW_CARD && saveCard && (
          <div className="flex flex-col gap-1.5 pl-1">
            <Label htmlFor={`${ids}-card-name`}>Card nickname</Label>
            <Input
              id={`${ids}-card-name`}
              value={cardName}
              maxLength={16}
              autoComplete="off"
              onChange={(e) => setCardName(e.target.value)}
              placeholder="Optional, e.g. Visa"
            />
          </div>
        )}
      </fieldset>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${ids}-email`}>Email receipt to</Label>
        <Input
          id={`${ids}-email`}
          type="email"
          inputMode="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Optional"
        />
      </div>

      {error && (
        <p role="alert" className="text-sm font-semibold text-red-700">
          {error}
        </p>
      )}

      <button
        type="submit"
        disabled={busy}
        className="min-h-12 w-full rounded-md px-4 py-2 text-[15px] font-bold text-white transition-opacity hover:opacity-90 disabled:opacity-60"
        style={{ backgroundColor: "var(--hs-red)" }}
      >
        {busy
          ? "Working…"
          : cardId === NEW_CARD
            ? "Continue to card details"
            : amount != null && !amountProblem
              ? `Pay ${usd.format(amount)}`
              : "Pay"}
      </button>
    </form>
  );
}

function CardOption(props: { name: string; checked: boolean; onSelect: () => void; label: string; detail?: string }) {
  return (
    <label
      className={`flex min-h-12 cursor-pointer items-center gap-3 rounded-lg border px-3 py-2 ${
        props.checked ? "border-healthsource-navy bg-healthsource-navy/5" : "border-healthsource-lightLavender bg-white"
      }`}
    >
      <input type="radio" name={props.name} checked={props.checked} onChange={props.onSelect} className="h-5 w-5" />
      <span className="flex flex-col">
        <span className="text-sm font-bold text-healthsource-dark">{props.label}</span>
        {props.detail && <span className="text-[13px] text-healthsource-gray">{props.detail}</span>}
      </span>
    </label>
  );
}

/** Mounts Fortis Commerce.js hosted card fields; card data goes straight to Fortis, never to our API. */
function HostedCardFields(props: {
  start: StatementNewCardStartResult;
  amount: number;
  onCancel: () => void;
  onDone: (transactionId: string) => Promise<void>;
}) {
  const { start, amount, onCancel, onDone } = props;
  const containerId = useId().replace(/:/g, "");
  const handled = useRef(false);
  const [posting, setPosting] = useState(false);

  useEffect(() => {
    const Commerce = window.Commerce;
    if (!Commerce) return;
    const elements = new Commerce.elements(start.clientToken);
    elements.create({
      container: `#${containerId}`,
      environment: start.environment,
      theme: "default",
      digitalWallets: [],
      fields: {
        billing: [
          { name: "address", required: true },
          { name: "postal_code", required: true },
        ],
      },
    });
    elements.on("done", async (result) => {
      const id = result?.data?.id;
      if (!id || handled.current) return;
      handled.current = true;
      setPosting(true);
      try {
        await onDone(id);
      } finally {
        setPosting(false);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [start.clientToken]);

  return (
    <div className="mt-5 flex flex-col gap-3 border-t border-healthsource-lightLavender pt-5">
      <div className="flex items-center justify-between">
        <h3 className="text-[15px] font-extrabold text-healthsource-dark">Card details</h3>
        <span className="text-[15px] font-bold text-healthsource-dark">{usd.format(amount)}</span>
      </div>
      {posting && (
        <p role="status" className="text-sm text-healthsource-gray">
          Confirming your payment…
        </p>
      )}
      <div id={containerId} className="min-h-[320px]" />
      {!posting && (
        <button
          type="button"
          onClick={onCancel}
          className="min-h-11 self-start text-sm font-bold text-healthsource-navy underline"
        >
          Cancel
        </button>
      )}
    </div>
  );
}
